"use client";
import { useSyncExternalStore, type ReactNode } from "react";
import Link from "@/components/ui/Link";
import { TagChipLink } from "@/components/stickers/TagChipLink";
import { formatCount, formatSizeMb } from "@/lib/posts-helpers";

type Record = {
  post_title: string;
  user_name: string;
  post_stickers?: number | null;
  post_like?: number | null;
  post_downloads?: number | null;
  post_comments?: number | null;
  post_size?: number | string | null;
};

export function useIsDesktop(query = "(min-width: 768px)") {
  return useSyncExternalStore(
    (notify) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", notify);
      return () => mql.removeEventListener("change", notify);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

export function DesktopOnly({ children }: { children: ReactNode }) {
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;
  return <>{children}</>;
}

export function StickerCardDesktopMeta({ record }: { record: Record }) {
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;
  return (
    <>
      <li aria-hidden className="bg-foreground/60 h-1 w-1 rounded-full" />
      <li>{formatCount(record.post_comments)} Comments</li>
      <li aria-hidden className="bg-foreground/60 h-1 w-1 rounded-full" />
      <li>{record.post_stickers ?? 0} Stickers</li>
      <li aria-hidden className="bg-foreground/60 h-1 w-1 rounded-full" />
      <li>{formatSizeMb(record.post_size)} Mb</li>
    </>
  );
}

export function StickerCardDesktopBlurb({ record, path }: { record: Record; path: string }) {
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;
  return (
    <p className="text-foreground text-[14px] leading-snug">
      <Link href={path} className="text-link hover:underline">
        {record.post_title}
      </Link>{" "}
      is a WhatsApp sticker pack published by{" "}
      <b className="text-foreground font-medium">{record.user_name}</b> and includes{" "}
      <b className="text-foreground font-medium">{record.post_stickers ?? 0}</b> high-quality
      stickers you can use in any chat.
      {/* Only with real engagement: "It has gone down well, with 0 people
          liking it" was false on most packs. Packs with counts keep the
          sentence byte for byte. */}
      {(record.post_like ?? 0) > 0 || (record.post_downloads ?? 0) > 0 ? (
        <>
          {" "}
          It has gone down well, with{" "}
          <b className="text-foreground font-medium">{record.post_like ?? 0}</b> people liking it
          and <b className="text-foreground font-medium">{record.post_downloads ?? 0}</b> who have
          already added these stickers to their WhatsApp conversations.
        </>
      ) : null}
    </p>
  );
}

export function StickerCardDesktopTags({ tags }: { tags: string[] }) {
  const isDesktop = useIsDesktop();
  if (!isDesktop || tags.length === 0) return null;
  return (
    <>
      {tags.map((tag) => (
        <TagChipLink key={tag} tag={tag} className="text-link hover:underline">
          #{tag}
        </TagChipLink>
      ))}
    </>
  );
}
